import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import { Switch } from './ui/switch.jsx';
import {
    ShieldCheck, ShieldAlert, ShieldQuestion, RefreshCw, Save, Activity,
} from 'lucide-react';
import { toast } from 'sonner';

import { parseUtc } from '../lib/parseUtc';
/**
 * SLOPrometheusSettings — per-cluster SLO Prometheus configuration card.
 *
 * Sister to the page-top SLODiscoveryBanner: the banner is the
 * first-time prompt ("Prom found, want to enable?"), this card is the
 * ongoing management surface (override URL, toggle auto-rollback,
 * inspect what was probed, force a re-check).
 *
 * State sources:
 *   - GET /api/clusters/{id}/slo/discovery  → prom_discovery + slo_config
 * Actions:
 *   - POST /api/clusters/{id}/slo/enable    → flip on, set URL
 *   - POST /api/clusters/{id}/slo/disable   → flip off (preserve config)
 */
export default function SLOPrometheusSettings({ clusterId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [manualUrl, setManualUrl] = useState('');
    const [autoRollback, setAutoRollback] = useState(true);

    const refresh = useCallback(async () => {
        if (!clusterId) return;
        setLoading(true);
        try {
            const resp = await axios.get(`${API}/clusters/${clusterId}/slo/discovery`);
            setData(resp.data);
            // Pre-fill the URL editor with whatever's currently configured
            // (or the discovered URL if SLO is off and no override yet).
            const cur = resp.data?.slo_config?.prometheus_url ||
                        resp.data?.prom_discovery?.url || '';
            setManualUrl(cur);
            setAutoRollback(resp.data?.slo_config?.auto_rollback_enabled ?? true);
        } catch (e) {
            // Silent — banner-style. Settings just stays empty.
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [clusterId]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleEnable = async () => {
        if (!manualUrl) {
            toast.error('Enter a Prometheus URL first');
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/clusters/${clusterId}/slo/enable`, {
                prometheus_url: manualUrl,
                auto_rollback_enabled: autoRollback,
            });
            toast.success('SLO-aware optimization enabled');
            await refresh();
        } catch (e) {
            toast.error(`Enable failed: ${e.response?.data?.detail || e.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDisable = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/clusters/${clusterId}/slo/disable`);
            toast.success('SLO-aware optimization disabled');
            await refresh();
        } catch (e) {
            toast.error(`Disable failed: ${e.response?.data?.detail || e.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleAutoRollbackToggle = async (on) => {
        if (!data?.slo_config?.enabled) {
            // Just update local state until SLO is enabled.
            setAutoRollback(on);
            return;
        }
        setSaving(true);
        setAutoRollback(on);
        try {
            await axios.post(`${API}/clusters/${clusterId}/slo/enable`, {
                prometheus_url: data.slo_config.prometheus_url,
                auto_rollback_enabled: on,
            });
            toast.success(`Auto-rollback ${on ? 'enabled' : 'disabled'}`);
            await refresh();
        } catch (e) {
            toast.error(`Update failed: ${e.response?.data?.detail || e.message}`);
            setAutoRollback(!on); // revert
        } finally {
            setSaving(false);
        }
    };

    if (loading && !data) return null;
    if (!clusterId) return null;

    const enabled = data?.slo_config?.enabled === true;
    const discoveredUrl = data?.prom_discovery?.url || '';
    const probedList = data?.prom_discovery?.probed || [];
    const foundAt = data?.prom_discovery?.found_at;
    const agentReported = data?.agent_last_registered_at;

    return (
        <Card className="bg-white/80 backdrop-blur-sm border-slate-200/50 shadow-lg">
            <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                    <Activity className="w-5 h-5 text-indigo-600" />
                    SLO-aware optimization · Prometheus
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                    Per-cluster setting. The agent probes well-known Prometheus
                    Service URLs hourly and reports what it finds. You can
                    override the URL or toggle the feature here.
                </p>
            </CardHeader>
            <CardContent className="space-y-5">
                <StatusBanner
                    enabled={enabled}
                    discoveredUrl={discoveredUrl}
                    foundAt={foundAt}
                    agentReported={agentReported}
                    currentUrl={data?.slo_config?.prometheus_url}
                />

                <div className="space-y-2">
                    <Label className="text-xs">Prometheus URL</Label>
                    <Input
                        value={manualUrl}
                        onChange={(e) => setManualUrl(e.target.value)}
                        placeholder="http://prometheus-server.monitoring.svc:80"
                        className="font-mono text-xs"
                    />
                    <p className="text-[11px] text-slate-500">
                        {discoveredUrl
                            ? `Auto-discovered: ${discoveredUrl}. Override above if you use a different Prometheus.`
                            : 'No Prometheus auto-discovered. Enter your URL manually.'}
                    </p>
                </div>

                <div className="flex items-center justify-between border-t border-slate-200 pt-4">
                    <div>
                        <div className="text-sm font-medium">Auto-rollback on regression</div>
                        <div className="text-[11px] text-slate-500">
                            When enabled, OptOps reverts a rightsizing change if golden
                            signals regress at both +5min and +15min checkpoints.
                        </div>
                    </div>
                    <Switch
                        checked={autoRollback}
                        onCheckedChange={handleAutoRollbackToggle}
                        disabled={saving}
                    />
                </div>

                <div className="flex gap-2 border-t border-slate-200 pt-4">
                    {enabled ? (
                        <>
                            <Button
                                variant="default"
                                onClick={handleEnable}
                                disabled={saving || !manualUrl}
                                className="flex items-center gap-2"
                            >
                                <Save className="w-4 h-4" />
                                Save URL
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleDisable}
                                disabled={saving}
                                className="flex items-center gap-2"
                            >
                                Disable SLO
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={handleEnable}
                            disabled={saving || !manualUrl}
                            className="flex items-center gap-2"
                        >
                            <ShieldCheck className="w-4 h-4" />
                            Enable SLO-aware optimization
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        onClick={refresh}
                        disabled={loading}
                        className="flex items-center gap-2 ml-auto"
                        title="Re-fetch the latest discovery state from the backend. The agent itself re-probes hourly; restart the agent Pod for an immediate refresh."
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>

                {probedList.length > 0 && (
                    <details className="text-xs text-slate-600">
                        <summary className="cursor-pointer font-medium">
                            Probed URLs ({probedList.length})
                        </summary>
                        <ul className="mt-2 ml-4 list-disc font-mono text-[11px]">
                            {probedList.map((u) => (
                                <li
                                    key={u}
                                    className={u === discoveredUrl ? 'text-green-700 font-semibold' : ''}
                                >
                                    {u}
                                    {u === discoveredUrl && ' ← responded'}
                                </li>
                            ))}
                        </ul>
                        <p className="text-[11px] text-slate-500 mt-2">
                            The agent stops probing as soon as one URL responds. Empty
                            list means the agent hasn't checked in yet. To re-probe,
                            restart the agent Pod or wait for the next hourly tick.
                        </p>
                    </details>
                )}
            </CardContent>
        </Card>
    );
}

// ---------- subcomponent ----------

function StatusBanner({ enabled, discoveredUrl, foundAt, agentReported, currentUrl }) {
    const fmt = (iso) => iso ? parseUtc(iso).toLocaleString() : 'never';

    if (enabled) {
        return (
            <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-green-700 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-green-900">
                    <div className="font-medium">SLO-aware optimization is ON</div>
                    <div className="mt-0.5">
                        Scraping <code className="font-mono bg-white/60 px-1 rounded">{currentUrl}</code>
                    </div>
                </div>
            </div>
        );
    }
    if (discoveredUrl) {
        return (
            <div className="rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 text-indigo-700 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-indigo-900">
                    <div className="font-medium">Prometheus detected — SLO is OFF</div>
                    <div className="mt-0.5">
                        Found at <code className="font-mono bg-white/60 px-1 rounded">{discoveredUrl}</code>
                        {foundAt && <span className="text-indigo-700"> · {fmt(foundAt)}</span>}
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 flex items-start gap-2">
            <ShieldQuestion className="w-4 h-4 text-slate-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-slate-700">
                <div className="font-medium">No Prometheus auto-detected</div>
                <div className="mt-0.5">
                    {agentReported
                        ? `Agent reported on ${fmt(agentReported)}. Enter a URL below to enable manually.`
                        : 'Waiting for the agent to register. Enter a URL below to bypass discovery.'}
                </div>
            </div>
        </div>
    );
}
